import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import type { DxTradeRawCandle } from '@model/DxTrade.types'

export type SparklineDataBySymbol = Record<string, number[] | undefined>

const SPARKLINE_HOURS = 24

const parseCandlesToPrices = (candles: DxTradeRawCandle[]): SparklineDataBySymbol => {
  const bySymbol: Record<string, DxTradeRawCandle[]> = {}
  candles.forEach((c) => {
    if (!bySymbol[c.symbol]) bySymbol[c.symbol] = []
    bySymbol[c.symbol].push(c)
  })

  const result: SparklineDataBySymbol = {}
  for (const symbol in bySymbol) {
    const sorted = bySymbol[symbol].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
    const closePrices = sorted.map((c) => c.closingPrice)
    result[symbol] = closePrices.length >= 2 ? closePrices : undefined
  }
  return result
}

export const useSparklineData = (
  symbols?: string[]
): {
  sparklineBySymbol: SparklineDataBySymbol
  isLoading: boolean
  fetchedSymbols: Set<string>
} => {
  const apiClient = useApiClient(ClientApiClient)
  const [sparklineBySymbol, setSparklineBySymbol] = useState<SparklineDataBySymbol>({})
  const [isLoading, setIsLoading] = useState(false)
  const fetchedRef = useRef<Set<string>>(new Set())

  const uniqueSymbols = useMemo(() => {
    if (!symbols?.length) return []
    return Array.from(new Set(symbols)).sort()
  }, [symbols])

  const newSymbols = useMemo(
    () => uniqueSymbols.filter((s) => !fetchedRef.current.has(s)),
    [uniqueSymbols]
  )

  const fetchSparklines = useCallback(
    async (symbolsToFetch: string[]) => {
      if (!symbolsToFetch.length) return

      symbolsToFetch.forEach((s) => fetchedRef.current.add(s))
      setIsLoading(true)

      try {
        const now = Math.floor(Date.now() / 1000)
        const from = now - SPARKLINE_HOURS * 60 * 60

        const candles = await apiClient.getDxtradeCandles(symbolsToFetch, '1hour', from, now)
        const parsed = parseCandlesToPrices(candles)

        setSparklineBySymbol((prev) => ({ ...prev, ...parsed }))
      } catch {
        // symbols stay in fetchedRef to prevent infinite retries
      } finally {
        setIsLoading(false)
      }
    },
    [apiClient]
  )

  useEffect(() => {
    if (newSymbols.length > 0) {
      fetchSparklines(newSymbols)
    }
  }, [newSymbols, fetchSparklines])

  const fetchedSymbols = useMemo(() => new Set(fetchedRef.current), [sparklineBySymbol])

  return {
    sparklineBySymbol,
    isLoading,
    fetchedSymbols,
  }
}
